import {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {format} from 'date-fns';
import {formatCurrency} from '@common/lib/utils';
import {
  ArrowLeft,
  Building2,
  FileText,
  DollarSign,
  Users,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  RefreshCw,
  ChevronRight,
  Receipt,
} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {trpc, RouterOutput} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {SubscriberSheet} from './SubscriberSheet';

type Subscriber = RouterOutput['internal']['invoice']['getSubscribers']['subscribers'][number];

const formatDate = (date: Date | null | undefined) => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'MMM d, yyyy');
  } catch {
    return 'N/A';
  }
};

export function InvoiceDetail() {
  const navigate = useNavigate();
  const {invoiceKey} = useParams<{invoiceKey: string}>();
  const {toast} = useToast();

  const [selectedSubscriberId, setSelectedSubscriberId] = useState<string | null>(null);

  const table = useDataTable({defaultPageSize: 20});

  const invoiceKeyNum = parseInt(invoiceKey || '0');

  const {
    data: invoiceData,
    isLoading: invoiceLoading,
    refetch: refetchInvoice,
  } = trpc.internal.invoice.getById.useQuery(
    {invoiceKey: invoiceKeyNum},
    {enabled: invoiceKeyNum > 0},
  );

  const {
    data: subscribersData,
    isLoading: subscribersLoading,
    isFetching: subscribersFetching,
    refetch: refetchSubscribers,
  } = trpc.internal.invoice.getSubscribers.useQuery(
    {invoiceKey: invoiceKeyNum, page: table.page, pageSize: table.pageSize},
    {enabled: invoiceKeyNum > 0},
  );

  const regenerateMutation = trpc.internal.invoice.regenerate.useMutation({
    onSuccess: () => {
      toast({title: 'Invoice regeneration started'});
      refetchInvoice();
      refetchSubscribers();
    },
    onError: (err) => {
      toast({title: 'Regeneration failed', description: err.message, variant: 'destructive'});
    },
  });

  const getStatusBadge = () => {
    if (!invoiceData) return null;
    const invoice = invoiceData.invoice;

    if (invoice.invoiceStatus === 'OPEN') {
      const isPastDue = invoice.dueDate && new Date(invoice.dueDate) < new Date();
      return isPastDue ? (
        <Badge className='bg-red-600 text-white border-0 font-medium text-sm px-3 py-1'>
          <AlertCircle className='h-4 w-4 mr-1' />
          Past Due
        </Badge>
      ) : (
        <Badge className='bg-amber text-white border-0 font-medium text-sm px-3 py-1'>
          <Clock className='h-4 w-4 mr-1' />
          Open
        </Badge>
      );
    }

    if (invoice.invoiceStatus === 'CLOSED') {
      return (
        <Badge className='bg-emerald text-white border-0 font-medium text-sm px-3 py-1'>
          <CheckCircle className='h-4 w-4 mr-1' />
          Closed
        </Badge>
      );
    }

    return (
      <Badge className='bg-brand-gray/10 text-brand-gray border-brand-gray/20 text-sm px-3 py-1'>
        {invoice.invoiceStatus || 'Unknown'}
      </Badge>
    );
  };

  const columns: Column<Subscriber>[] = [
    {
      id: 'name',
      header: 'Subscriber',
      render: (sub) => (
        <div>
          <div className='font-medium text-brand-navy'>{sub.name}</div>
          <div className='text-xs text-brand-gray font-mono'>{sub.subscriberId}</div>
        </div>
      ),
    },
    {
      id: 'alternateId',
      header: 'Alternate ID',
      render: (sub) => (
        <span className='font-mono text-sm text-brand-gray'>{sub.alternateId || '-'}</span>
      ),
    },
    {
      id: 'rateCount',
      header: 'Rates',
      render: (sub) => <span className='font-mono'>{sub.rateCount}</span>,
    },
    {
      id: 'premiumTotal',
      header: 'Premium',
      render: (sub) => (
        <span className='font-mono font-medium text-brand-navy'>
          {formatCurrency(sub.premiumTotal)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      render: (sub) => (
        <Button
          size='sm'
          variant='ghost'
          className='text-brand-blue hover:text-brand-blue-dark'
          onClick={() => setSelectedSubscriberId(sub.subscriberId)}
        >
          View Breakdown
          <ChevronRight className='h-4 w-4 ml-1' />
        </Button>
      ),
    },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title={`Invoice ${invoiceData?.invoice.invoiceNumber || `#${invoiceKey}`}`}
          description={
            invoiceData?.group
              ? `${invoiceData.group.name} | ${invoiceData.invoice.groupId}`
              : invoiceData?.invoice.groupId
          }
          icon={<Receipt className='h-6 w-6' />}
          isFetching={subscribersFetching}
          isLoading={invoiceLoading}
        >
          {getStatusBadge()}
          <PageHeaderAction tone='secondary' onClick={() => navigate('/invoices')}>
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back to Invoices
          </PageHeaderAction>
          <PageHeaderAction
            tone='secondary'
            onClick={() => {
              refetchInvoice();
              refetchSubscribers();
            }}
          >
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh
          </PageHeaderAction>
        </PageHeader>

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          {/* Summary Cards */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-2'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <DollarSign className='h-4 w-4 text-emerald' />
                  Total Premium
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoiceLoading ? (
                  <Skeleton className='h-8 w-24' />
                ) : (
                  <div className='text-2xl font-bold text-brand-navy'>
                    {formatCurrency(invoiceData?.invoice.premium || 0)}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-2'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <DollarSign className='h-4 w-4 text-brand-blue' />
                  Amount Due
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoiceLoading ? (
                  <Skeleton className='h-8 w-24' />
                ) : (
                  <div className='text-2xl font-bold text-brand-navy'>
                    {formatCurrency(invoiceData?.invoice.amountDue || 0)}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-2'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <CheckCircle className='h-4 w-4 text-emerald' />
                  Received
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoiceLoading ? (
                  <Skeleton className='h-8 w-24' />
                ) : (
                  <div className='text-2xl font-bold text-emerald'>
                    {formatCurrency(invoiceData?.invoice.received || 0)}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-2'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <AlertCircle className='h-4 w-4 text-red-500' />
                  Outstanding
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoiceLoading ? (
                  <Skeleton className='h-8 w-24' />
                ) : (
                  <div
                    className={`text-2xl font-bold ${
                      (invoiceData?.invoice.amountDue || 0) -
                        (invoiceData?.invoice.paymentPosted || 0) >
                      0
                        ? 'text-red-600'
                        : 'text-emerald'
                    }`}
                  >
                    {formatCurrency(
                      (invoiceData?.invoice.amountDue || 0) -
                        (invoiceData?.invoice.paymentPosted || 0),
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Invoice Info Section */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
            {/* Invoice Details */}
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='bg-gray-50 border-b'>
                <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                  <FileText className='h-5 w-5 text-brand-blue' />
                  Invoice Details
                </CardTitle>
              </CardHeader>
              <CardContent className='p-4'>
                {invoiceLoading ? (
                  <div className='space-y-3'>
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className='h-6 w-full' />
                    ))}
                  </div>
                ) : (
                  <div className='grid grid-cols-2 gap-4 text-sm'>
                    <div>
                      <div className='text-brand-gray mb-1'>Invoice Date</div>
                      <div className='font-medium text-brand-navy flex items-center gap-2'>
                        <Calendar className='h-4 w-4 text-brand-gray' />
                        {formatDate(invoiceData?.invoice.invoiceDate)}
                      </div>
                    </div>
                    <div>
                      <div className='text-brand-gray mb-1'>Due Date</div>
                      <div className='font-medium text-brand-navy flex items-center gap-2'>
                        <Calendar className='h-4 w-4 text-brand-gray' />
                        {formatDate(invoiceData?.invoice.dueDate)}
                      </div>
                    </div>
                    <div>
                      <div className='text-brand-gray mb-1'>Billing Period</div>
                      <div className='font-medium text-brand-navy'>
                        {formatDate(invoiceData?.invoice.billingFrom)} -{' '}
                        {formatDate(invoiceData?.invoice.billingThru)}
                      </div>
                    </div>
                    <div>
                      <div className='text-brand-gray mb-1'>Invoice Level</div>
                      <div className='font-medium text-brand-navy'>
                        {invoiceData?.invoice.invoiceLevel === 'G'
                          ? 'Group'
                          : invoiceData?.invoice.invoiceLevel === 'D'
                            ? 'Division'
                            : invoiceData?.invoice.invoiceLevel === 'S'
                              ? 'Subscriber'
                              : invoiceData?.invoice.invoiceLevel || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className='text-brand-gray mb-1'>Subscribers</div>
                      <div className='font-medium text-brand-navy flex items-center gap-2'>
                        <Users className='h-4 w-4 text-brand-gray' />
                        {invoiceData?.invoice.subscriberCount || 0}
                      </div>
                    </div>
                    <div>
                      <div className='text-brand-gray mb-1'>Members</div>
                      <div className='font-medium text-brand-navy flex items-center gap-2'>
                        <Users className='h-4 w-4 text-brand-gray' />
                        {invoiceData?.invoice.memberCount || 0}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Group Info */}
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='bg-gray-50 border-b'>
                <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                  <Building2 className='h-5 w-5 text-brand-blue' />
                  Group Information
                </CardTitle>
              </CardHeader>
              <CardContent className='p-4'>
                {invoiceLoading ? (
                  <div className='space-y-3'>
                    {[...Array(4)].map((_, i) => (
                      <Skeleton key={i} className='h-6 w-full' />
                    ))}
                  </div>
                ) : invoiceData?.group ? (
                  <div className='space-y-3 text-sm'>
                    <div>
                      <div className='text-brand-gray mb-1'>Group Name</div>
                      <div className='font-medium text-brand-navy'>{invoiceData.group.name}</div>
                    </div>
                    <div>
                      <div className='text-brand-gray mb-1'>Group ID (VBA)</div>
                      <div className='font-mono text-brand-navy'>{invoiceData.invoice.groupId}</div>
                    </div>
                    <div>
                      <div className='text-brand-gray mb-1'>Client</div>
                      <div className='font-medium text-brand-navy'>
                        {invoiceData.group.clientName}
                      </div>
                    </div>
                    {invoiceData.invoice.divisionId && (
                      <div>
                        <div className='text-brand-gray mb-1'>Division ID</div>
                        <div className='font-mono text-brand-navy'>
                          {invoiceData.invoice.divisionId}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='text-sm'>
                    <div>
                      <div className='text-brand-gray mb-1'>Group ID (VBA)</div>
                      <div className='font-mono text-brand-navy'>
                        {invoiceData?.invoice.groupId || 'N/A'}
                      </div>
                    </div>
                    <p className='text-brand-gray mt-3'>Group not found in the system database.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className='flex gap-3 mb-6'>
            <Button
              variant='outline'
              onClick={() => regenerateMutation.mutate({invoiceKey: invoiceKeyNum})}
              disabled={regenerateMutation.isPending || invoiceKeyNum === 0}
              className='border-gray-300'
            >
              {regenerateMutation.isPending ? (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              ) : (
                <RefreshCw className='h-4 w-4 mr-2' />
              )}
              Regenerate Invoice
            </Button>
          </div>

          {/* Subscribers Table */}
          <DataTable<Subscriber>
            table={table}
            columns={columns}
            data={subscribersData?.subscribers}
            total={subscribersData?.totalCount}
            totalPages={subscribersData?.totalPages}
            loading={subscribersLoading}
            emptyMessage='No subscribers found'
            title='Subscribers'
            entity={{singular: 'subscriber', plural: 'subscribers'}}
            rowKey={(sub) => sub.subscriberId}
          />
        </div>
      </div>

      {/* Subscriber Sheet */}
      {selectedSubscriberId && invoiceKeyNum > 0 && (
        <SubscriberSheet
          invoiceKey={invoiceKeyNum}
          subscriberId={selectedSubscriberId}
          onClose={() => setSelectedSubscriberId(null)}
        />
      )}
    </div>
  );
}
